import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Check, Facebook, MessageCircle, Mail } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const SiteFooter: React.FC = () => {
  const { t } = useTranslation();
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterDone, setNewsletterDone] = useState(false);

  const columns = [
    {
      title: t("footer_nav", "Navigation"),
      links: [
        { label: t("nav_horses"), to: "/horses" },
        { label: t("nav_archives", "Archives"), to: "/archives" },
        { label: t("nav_about"), to: "/about" },
        { label: t("nav_faq"), to: "/faq" },
        { label: t("nav_contact"), to: "/contact" },
        { label: t("nav_info"), to: "/info" },
      ],
    },
    {
      title: t("footer_legal", "Légal"),
      links: [
        { label: t("nav_legal"), to: "/legal" },
        { label: t("nav_cgu"), to: "/cgu" },
        { label: t("nav_cga"), to: "/cga" },
      ],
    },
    {
      title: t("footer_policies", "Politiques"),
      links: [
        { label: t("nav_privacy"), to: "/privacy" },
        { label: t("nav_cookies"), to: "/cookies" },
        { label: t("nav_return"), to: "/return-policy" },
        { label: t("nav_refund"), to: "/refund-policy" },
      ],
    },
  ];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const emailInput = form.elements.namedItem("newsletter_email") as HTMLInputElement;
    const email = emailInput.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("newsletter_invalid_email"));
      return;
    }
    setNewsletterLoading(true);
    const { error } = await supabase.from("newsletter_subscribers").insert({ email });
    setNewsletterLoading(false);
    if (error) {
      toast(error.code === "23505" ? t("newsletter_already") : t("newsletter_error"));
      return;
    }
    setNewsletterDone(true);
    emailInput.value = "";
    toast.success(t("newsletter_success"));
    setTimeout(() => setNewsletterDone(false), 4000);
  };

  return (
    <footer className="relative bg-foreground text-background">
      {/* Curved teal transition */}
      <div className="absolute -top-10 left-0 right-0 h-10 overflow-hidden">
        <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" preserveAspectRatio="none">
          <path d="M0 40V20C360 0 1080 0 1440 20V40H0Z" className="fill-foreground" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 pb-8">
        {/* Logo + tagline centered */}
        <div className="flex flex-col items-center mb-10">
          <LocalizedLink to="/" className="flex items-center gap-3 mb-3">
            <img src="/images/logo-horsevault.png" alt="HorseVault®" className="h-14 w-14 object-contain" width={56} height={56} loading="lazy" />
            <span className="text-lg font-bold tracking-tight text-background">HorseVault®</span>
          </LocalizedLink>
          <p className="text-xs text-background/50 max-w-sm text-center leading-relaxed">{t("footer_tagline")}</p>
        </div>

        {/* Newsletter centered */}
        <div className="flex flex-col items-center mb-10">
          <p className="text-xs font-semibold text-background/80 uppercase tracking-widest mb-3">{t("subscribe")}</p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row items-center gap-2 w-full max-w-md">
            <input name="newsletter_email" type="email" placeholder={t("email")}
              className="flex-1 w-full px-4 py-2.5 rounded-lg border border-background/20 bg-background/10 text-background placeholder:text-background/40 text-sm focus:outline-none focus:border-background/50 transition-colors" />
            <button type="submit" disabled={newsletterLoading}
              className="bg-primary text-primary-foreground px-6 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap">
              {newsletterLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : newsletterDone ? <Check className="w-4 h-4" /> : t("subscribe")}
            </button>
          </form>
        </div>

        {/* Social icons */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {[
            { icon: <Facebook className="w-4 h-4" />, href: "https://www.facebook.com/elitehorseselection", label: "Facebook" },
            { icon: <MessageCircle className="w-4 h-4" />, href: "https://wa.me/351961196405", label: "WhatsApp" },
            { icon: <Mail className="w-4 h-4" />, href: "mailto:info@horsevault.com", label: "Email" },
          ].map((s, i) => (
            <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
              className="w-11 h-11 rounded-full border border-primary/40 flex items-center justify-center text-primary/80 hover:text-primary hover:border-primary transition-colors">
              {s.icon}
            </a>
          ))}
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 border-t border-background/10 pt-8 mb-8">
          {columns.map((col) => (
            <div key={col.title}>
              <h3 className="text-[11px] font-semibold text-background/80 uppercase tracking-widest mb-3">{col.title}</h3>
              <ul className="space-y-2">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <LocalizedLink to={link.to} className="text-xs text-background/50 hover:text-background transition-colors">
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="border-t border-background/10 pt-6 text-center">
          <p className="text-[11px] text-background/60">
            © {new Date().getFullYear()} HorseVault® · {t("all_rights_reserved")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
